import React, { useMemo, Fragment } from "react";
import { components } from 'common';
import { lang } from '@umu/umu-util';
import { classSet } from '@umu/umu-dom';
import { CERTIFICATE_FORMAT, CERTIFICATE_IMG_TYPE } from '../../interface/index';
import { QrCodeContent } from '../../components/index';
import { QR_CODE_WIDTH, convertImage2PngFormat } from '../../dataflow/index';
import { getStudyTemplateQrCodeParams, DEFAULT_SCALE_RATIO, getCertificateIssueTime } from '../../dataflow/common';
import { ICertificateThemeProps } from '../../interface/common';
import { QR_CODE_HORIZONTAL_WIDTH } from "../../dataflow/enterprise";

const { UMUBg, If } = components;

const CustomStudyTheme1 = (props: ICertificateThemeProps) => {
    const { themeInfo, scaleRatio = DEFAULT_SCALE_RATIO } = props;
    const {
        certificateName,
        certificateText,
        enterpriseInfo,
        certificateDisplayConfig: conf,
        traineesName,
        displayStyle = CERTIFICATE_FORMAT.VERTICAL,
        traineesSubName,
        courseInfo = {
            courseName: ''
        },
    } = themeInfo;
    const {isVertical, isHorizontal} = useMemo(() => {
        return {
            isVertical: Number(displayStyle) === CERTIFICATE_FORMAT.VERTICAL,
            isHorizontal: Number(displayStyle) === CERTIFICATE_FORMAT.HORIZONTAL
        }
    }, [displayStyle]);
    const getEnterpriseLogo = () => {
        return enterpriseInfo.logoType === CERTIFICATE_IMG_TYPE.IMG_TYPE ? (
            <UMUBg clsName="certificate-enterprise-logo" bgSrc={convertImage2PngFormat(enterpriseInfo.logo)} />
        ) : (
            enterpriseInfo.logo
        );
    };
    const logoContent = useMemo(() => {
        return !!enterpriseInfo.logo ? getEnterpriseLogo() : <div className="empty-logo" />;
    }, [enterpriseInfo.logo, enterpriseInfo.logoType]);

    const certificateNameContent = useMemo(() => {
        const isHasCertificateName = !!certificateName;
        return (
            <div
                className={classSet({
                    'certificate-title text-row-more text-row-2 custom-certificate-title': isHasCertificateName,
                    'empty-certificate-name': !isHasCertificateName,
                })}
            >
                {certificateName || ''}
            </div>
        );
    }, [certificateName]);

    const traineesNameContent = useMemo(() => {
        const isShow = !!traineesName || !!traineesSubName;
        return (
            <div className={`${isShow ? '' : 'empty-'}trainees-name content-trainees-name-split`}>
                <If condition={!!traineesName}>
                    <div className="text-row-1 title-name">{traineesName}</div>
                </If>
                <If condition={!!traineesSubName && !!conf.isShowTraineesSubName}>
                    <div className="text-row-more text-row-2 trainees-sub-name">{traineesSubName}</div>
                </If>
            </div>
        );
    }, [traineesName, traineesSubName, conf.isShowTraineesSubName]);

    const renderBasicInfo = () => {
        const text = lang('《{$0}》').replace('{$0}', courseInfo.courseName);

        return (
            <Fragment>
                {logoContent}
                {certificateNameContent}
                <div className="certificate-main-content">
                    {traineesNameContent}
                    <div className="certificate-text text-row-more">{certificateText}</div>
                    <If condition={!!conf.isShowCourseName || !!conf.isShowSourceName}>
                        <div className="course-name content-course-name-split text-row-more text-row-2">{text}</div>
                    </If>
                </div>
            </Fragment>
        );
    };
    const emptyElement = (<div className="custom-label issue-footer-col"></div>)
    const renderCertificateIssueFooter = (ele: JSX.Element | null) => {
        const isShowQr = !!conf.isShowCertificateQRCode;
        const isShowContent = isShowQr || !!ele
        const isShowIssueTime = !!conf.isShowLecturer && !!conf.setUpInfo && !!conf.setUpInfo.lecturerName;
        const isShowUnit = !!enterpriseInfo.issuingUnitName;
        const isOnlyShowOne = isShowContent && !isShowIssueTime && isShowUnit ||
            isShowIssueTime && !isShowContent && !isShowUnit ||
            isShowUnit && !isShowContent && !isShowIssueTime;
        return (
            <div
                className={classSet({
                    'certificate-issue-footer': true,
                    'certificate-issue-show-only-one': isOnlyShowOne
                })}
            >
                {isShowIssueTime ? <div className="custom-label issue-footer-col">
                    <div className="certificate-issue-title">
                        {conf.setUpInfo ? conf.setUpInfo.lecturerName : ''}
                    </div>
                    <div className="certificate-issue-sub">{conf.setUpInfo?.lecturerSubName || lang('课程讲师')}</div>
                </div> : isShowContent && emptyElement}

                <If condition={isShowContent}>
                    <div className="certificate-issue-qr issue-footer-col">
                        {!!ele && <div className="certificate-issue-date">{ele}</div>}
                        {isShowQr && <QrCodeContent
                            id="certificateId"
                            className={`custom-certificate-issue-qr-code-${displayStyle}`}
                            codeOptions={{
                                text: conf.setUpInfo ? conf.setUpInfo.QRCodeUrl : 'https://umu.cn',
                                ...getStudyTemplateQrCodeParams(scaleRatio, isHorizontal ? QR_CODE_HORIZONTAL_WIDTH : QR_CODE_WIDTH)
                            }}
                        />}
                    </div>
                </If>

                {isShowUnit ? <div className="custom-label issue-footer-col">
                    <div className="certificate-issue-title">{enterpriseInfo.issuingUnitName}</div>
                    <div className="certificate-issue-sub">{lang('颁发单位')}</div>
                </div> : isShowContent && emptyElement}
            </div>
        );
    };

    const renderCertificateIssueInfo = () => {
        const isShowCertificateIssueTime = conf.isShowCertificateIssueTime;
        const grantTs = Number(conf.setUpInfo?.certificateIssueTime || '0');
        const validPeriodTimeContent = isShowCertificateIssueTime ? (
            <div className="certificate-issue-validity-time">
                <p className="certificate-issue-title">
                    {isHorizontal && `${lang('日期')}: `}{getCertificateIssueTime(grantTs)}
                </p>
                <If condition={isVertical}>
                    <p className="certificate-issue-sub">{lang('日期')}</p>
                </If>

            </div>
        ) : null;

        return (
            <div className="certificate-issue-info">
                {renderCertificateIssueFooter(validPeriodTimeContent)}
            </div>
        );
    };

    return (
        <div className="certificate-view-info certificate-basic-info certificate-custom-view-info">
            {renderBasicInfo()}
            {renderCertificateIssueInfo()}
        </div>
    );

}

export {
    CustomStudyTheme1
}
